import React from "react"
import { NavigationArrow } from "@phosphor-icons/react"
import { cn } from "@/lib/utils"
import { RanksBackground } from "@/modules/productivity-cockpit/components/ranks-background"
import {
  ARCS_RADIUS,
  buildBackground,
  buildDividerTransform,
} from "@/modules/productivity-cockpit/helpers/build-gauge-chart"
import { COLOR_RANGE_LIST } from "@/modules/productivity-cockpit/types/ColorRanges"
import { GAUGE_CHART_LIST, GaugeChartType } from "@/modules/productivity-cockpit/types/GaugeChart"

interface GaugeChartProps extends React.HTMLAttributes<HTMLDivElement> {
  value: number
  maxValue?: number
  valueClassName?: string
  hideSubDivider?: boolean
  hideHighlight?: boolean
  name: GaugeChartType
  ref?: React.Ref<HTMLDivElement>
}

export const GaugeChart = ({
  className,
  name,
  title,
  value,
  maxValue,
  valueClassName,
  hideSubDivider,
  hideHighlight,
  ref,
  ...props
}: GaugeChartProps) => {
  const { colorRanges, min, step: defaultStep } = GAUGE_CHART_LIST[name]
  const max = maxValue ?? GAUGE_CHART_LIST[name].max
  const step = maxValue ? max / 8 : defaultStep
  const numberOfArcsPoints = Math.trunc((max - min) / step)
  const arcDegree = ARCS_RADIUS / numberOfArcsPoints
  const startDegree = ARCS_RADIUS / -2
  const actualValue = value > max ? max : value

  const separatorRanges = Array.from({ length: numberOfArcsPoints }, (_, index) => index)

  const computedBackgroundDefault = buildBackground(
    min,
    max,
    step,
    separatorRanges,
    colorRanges,
    actualValue,
    arcDegree,
    startDegree,
  )

  const { color, ...computedBackgroundHighlight } = buildBackground(
    min,
    max,
    step,
    separatorRanges,
    colorRanges,
    actualValue,
    arcDegree,
    startDegree,
    true,
  )

  const styleTextColor = () => {
    switch (color) {
      case COLOR_RANGE_LIST.YELLOW:
        return "text-on-warning-surface"
      case COLOR_RANGE_LIST.CRITICAL:
        return "text-on-critical"
      default:
        return "text-on-surface"
    }
  }

  return (
    <div ref={ref} className={cn("grid scale-[0.85] gap-2", className)} {...props}>
      <div
        data-testid={`${name}-trigger`}
        className="from-surface to-surface-subdued outline-surface-subdued relative z-0 flex h-60 w-60 items-center justify-center rounded-full bg-linear-to-b shadow-xl outline-offset-4 outline-solid">
        {!hideHighlight && (
          <>
            {/* Ranks background default */}
            <RanksBackground className="z-[-1] opacity-20" style={computedBackgroundDefault} />

            {/* Ranks background highlight */}
            <RanksBackground style={computedBackgroundHighlight} />
          </>
        )}

        {/* Small Circle */}
        <div className="from-surface to-surface-subdued absolute top-1/2 left-1/2 h-36 w-36 -translate-x-1/2 -translate-y-1/2 rounded-full bg-linear-to-b"></div>
        <div className="absolute top-[calc(100%-2.5rem)] left-1/2 grid -translate-x-1/2 text-center select-none">
          <span className="text-on-surface -mt-1 text-base leading-tight font-medium">{title}</span>
        </div>

        {/* Color in Small Circle */}
        <div
          className="absolute top-1/2 left-1/2 z-10 grid -translate-x-1/2 -translate-y-1/2 text-center select-none"
          style={{ color, textShadow: "0 1px 1px var(--shadow-color)" }}>
          {/* Following Highlight Color from Big Circle for Value */}
          <div className={cn("text-[32px] font-medium", valueClassName)}>
            <div data-testid={`${name}-value`} className={valueClassName && styleTextColor()}>
              {value}
            </div>
          </div>
        </div>
        <div className="from-surface-subdued to-surface absolute h-32 w-32 rounded-full bg-linear-to-b shadow-inner select-none"></div>

        {/* Divider and SubDivider */}
        {[...Array(numberOfArcsPoints + 1)].map((_, index) => {
          const indicator = min + index * step
          const decimalPart = indicator.toString().split(".")[1] ?? "0"
          return (
            <div key={`${index + 1}`}>
              <div
                className="absolute bottom-1/2 left-1/2 flex origin-bottom flex-col items-center justify-center gap-1"
                style={{
                  ["--radius" as string]: "4.5rem",
                  transform: buildDividerTransform(index, arcDegree, startDegree),
                }}>
                <div className="bg-on-surface relative h-2 w-0.5 rounded-full"></div>
                <span
                  className="text-on-surface relative z-10 flex h-4 items-center justify-center text-xs font-semibold"
                  style={{ transform: `rotate(${(startDegree + index * arcDegree) * -1}deg)` }}>
                  {decimalPart.length > 1 ? indicator.toFixed(1) : indicator}
                </span>
                <div className="bg-on-surface relative h-2 w-0.5 rounded-full"></div>
              </div>
              {index < numberOfArcsPoints && !hideSubDivider && (
                <div
                  className="bg-on-surface absolute bottom-1/2 left-1/2 flex h-2 w-0.5 origin-bottom items-center justify-center rounded-full opacity-30"
                  style={{
                    ["--radius" as string]: "6.5rem",
                    transform: buildDividerTransform(index + 0.5, arcDegree, startDegree),
                  }}></div>
              )}
            </div>
          )
        })}

        {/* Arrow */}
        <div
          className="text-primary absolute bottom-1/2 left-1/2 z-2 h-20 w-0.75 origin-bottom rounded-full transition-all"
          style={{
            transform: `translateX(-50%) rotate(${startDegree + ((actualValue - min) / step) * arcDegree}deg)`,
          }}>
          <div className="absolute top-0 left-1/2 -translate-x-1/2">
            <NavigationArrow weight="fill" className="rotate-45" />
          </div>
        </div>
      </div>
    </div>
  )
}
